// ===============================================================
// ✅ SelectGenderPage.js — Livora (Name + Gender + Birthdate) SERVER-ONLY
// path: src/components/SelectGenderPage.js
// ===============================================================
// SIMPLIFIED: this screen no longer performs, displays, or depends on
// any country detection/preview at all. Country is never shown,
// selected, or blocked on here - it is detected client-side (a direct
// browser/WebView request to ipapi.co - see src/utils/geo.js's
// detectUserCountry()) and persisted on a bounded background schedule
// by src/context/AccountSessionStateContext.js, entirely independent of
// this screen's submission flow. completeOnboarding itself never
// resolves country live either (see functions/auth/completeOnboarding.js);
// it writes gender/birthDate immediately and leaves
// countryResolutionStatus "pending" when there is no existing lock.
// Continue depends ONLY on the onboarding fields themselves (name,
// gender, birthdate, consent) plus the submit-in-flight guard below -
// nothing here can ever be blocked by a country call, because there is
// no country call on this screen anymore.
//
// DISPLAY NAME: uses the SAME canonical `name` field users/{uid} and
// publicProfiles/{uid} already use everywhere else (EditProfileForm.js,
// MyProfilePage.js, chat/calls/discovery/admin reads - see
// functions/lib/publicProfile.js's buildPublicProfileFromUser, which
// mirrors users/{uid}.name into publicProfiles/{uid}.name automatically
// on every write via the existing functions/triggers/
// syncUserPublicProfile.js trigger - no new cross-document contract).
// There is no separate `displayName` field written here - Firebase
// Auth's own `auth.currentUser.displayName` (Google Sign-In) is read
// ONLY as an optional prefill source, never written back to Auth, and
// only ever flows into Firestore's `name` field after passing the same
// validateDisplayName() normalization/validation this screen applies to
// manual input. The exact same rules live in src/utils/displayName.js,
// shared with EditProfileForm.js so the two screens can never drift
// apart. Name is required by THIS client before Continue can submit,
// but completeOnboarding.js (server) still accepts an old v11 request
// with no `name` field at all - see that file's header.

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import { auth, db } from "../firebase";
import { doc, getDoc, Timestamp, updateDoc } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import femaleAvatar from "../assets/avatars/female.png";
import maleAvatar from "../assets/avatars/male.png";

import "./SelectGenderPage.css";
import { normalizeDigits } from "../utils/digits";
import { parseBirthDateParts, calculateAgeFromBirthDateString } from "../utils/localizedDate";
import { NAME_MAX_LENGTH, validateDisplayName } from "../utils/displayName";
import { useAccountSessionState } from "../context/AccountSessionStateContext";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isoToDisplay(iso) {
  if (!iso || !ISO_DATE_RE.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

function formatBirthdateTyping(raw) {
  const digits = normalizeDigits(raw).replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

// Plausibility guard only (rejects e.g. a mistyped year like "0091") -
// the actual calendar-date realism check (leap days, days-per-month,
// etc.) is NOT duplicated here; it is delegated to the shared
// parseBirthDateParts() below, which uses the exact same UTC
// Date.UTC()-round-trip rollover check as the server's own
// parseBirthDate() in functions/auth/completeOnboarding.js.
const MIN_PLAUSIBLE_BIRTH_YEAR = 1900;

function parseDisplayToIso(text) {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(normalizeDigits(String(text || "").trim()));
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (year < MIN_PLAUSIBLE_BIRTH_YEAR) return null;
  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return parseBirthDateParts(iso) ? iso : null;
}

function validateBirthdateDisplay(text, todayISO) {
  const iso = parseDisplayToIso(text);
  if (!iso) {
    return { ok: false, iso: null, errorKey: "gender.invalid_birthdate" };
  }
  if (iso > todayISO) {
    return { ok: false, iso: null, errorKey: "gender.birthdate_future" };
  }
  const age = calculateAgeFromBirthDateString(iso);
  if (age === null || age < 18) {
    return { ok: false, iso: null, errorKey: "gender.birthdate_under_18" };
  }
  return { ok: true, iso, errorKey: null };
}

const SelectGenderPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { refresh: refreshAccountSessionState } = useAccountSessionState();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [legalConsentChecked, setLegalConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [name, setName] = useState("");

  const nativeDateRef = useRef(null);
  const storedBirthIso = localStorage.getItem("birthDate") || "";

  const [birthDateText, setBirthDateText] = useState(() =>
    isoToDisplay(storedBirthIso)
  );
  const [birthDate, setBirthDate] = useState(
    ISO_DATE_RE.test(storedBirthIso) ? storedBirthIso : ""
  );

  const submitInFlightRef = useRef(false);

  // Deliberately kept as `toISOString()` (UTC), not a local-time
  // equivalent: this is the "today" boundary used both for the native
  // date input's `max` attribute and for the future-birthdate rejection
  // in validateBirthdateDisplay below, and it must match the server's
  // own UTC "now" policy (functions/auth/completeOnboarding.js's
  // isAdult18, and calculateAgeFromCalendarParts in
  // ../utils/localizedDate.js) exactly - a local-time "today" would let
  // a viewer west of UTC submit a birthdate the server still considers
  // one calendar day in the future, and vice versa east of UTC.
  const todayISO = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      const userRef = doc(db, "users", user.uid);

      try {
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          navigate("/login");
          return;
        }

        const data = snap.data() || {};
        if (data.gender) {
          navigate("/home");
          return;
        }

        // Prefill only when the Google-provided displayName already
        // passes the SAME rules manual input must pass - an invalid
        // value (empty, whitespace-only, or over NAME_MAX_LENGTH) is
        // silently left blank rather than prefilled, so the field is
        // never pre-populated with something Continue would reject.
        const googleNameCheck = validateDisplayName(user.displayName);
        if (googleNameCheck.ok) {
          setName(googleNameCheck.value);
        }

        setLoading(false);
      } catch (e) {
        console.error("SelectGenderPage getDoc error:", e);
        navigate("/login");
      }
    };

    run();
  }, [navigate]);

  const handleContinue = async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Synchronous ref guard (not just the busy state): two clicks fired
    // in the same tick, before React re-renders the disabled attribute,
    // must still result in completeOnboarding being invoked at most
    // once for this attempt. Continue depends ONLY on this guard plus
    // the field validation below - no country call exists on this
    // screen to depend on.
    if (busy || submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setBusy(true);

    const nameValidation = validateDisplayName(name);
    if (!nameValidation.ok) {
      alert(
        t(nameValidation.errorKey, {
          defaultValue:
            nameValidation.errorKey === "gender.name_too_long"
              ? "Name is too long."
              : "Enter your name first.",
        })
      );
      submitInFlightRef.current = false;
      setBusy(false);
      return;
    }

    if (!legalConsentChecked) {
      setConsentError(
        t("gender.consent_required_error", {
          defaultValue:
            "You must agree to the Terms and Privacy Policy to continue.",
        })
      );
      submitInFlightRef.current = false;
      setBusy(false);
      return;
    }

    setConsentError("");

    if (!String(birthDateText || "").trim()) {
      alert(t("gender.birthdate_required", { defaultValue: "Enter your birthdate first." }));
      submitInFlightRef.current = false;
      setBusy(false);
      return;
    }

    const birthValidation = validateBirthdateDisplay(birthDateText, todayISO);
    if (!birthValidation.ok) {
      alert(
        t(birthValidation.errorKey, {
          defaultValue:
            birthValidation.errorKey === "gender.birthdate_under_18"
              ? "You must be at least 18 years old."
              : birthValidation.errorKey === "gender.birthdate_future"
              ? "Birthdate cannot be in the future."
              : "Enter a valid birthdate (DD.MM.YYYY).",
        })
      );
      submitInFlightRef.current = false;
      setBusy(false);
      return;
    }

    if (selectedGender !== "female" && selectedGender !== "male") {
      alert(t("gender.gender_required", { defaultValue: "Select your gender to continue." }));
      submitInFlightRef.current = false;
      setBusy(false);
      return;
    }

    const birthDateIso = birthValidation.iso;
    setBirthDate(birthDateIso);
    localStorage.setItem("birthDate", birthDateIso);

    try {
      const functions = getFunctions(undefined, "us-central1");
      const completeOnboarding = httpsCallable(functions, "completeOnboarding");

      // No country value is sent at all, and none is read back either -
      // country is detected later, in the background, via a direct
      // client-side lookup (see src/utils/geo.js and
      // src/context/AccountSessionStateContext.js).
      await completeOnboarding({
        name: nameValidation.value,
        gender: selectedGender,
        birthDate: birthDateIso,
      });

      try {
        await updateDoc(doc(db, "users", user.uid), {
          legalConsent: {
            accepted: true,
            acceptedAt: Timestamp.now(),
            termsVersion: "terms_v1",
            privacyVersion: "privacy_v1",
            locale: String(i18n.language || "en").slice(0, 24),
          },
        });
      } catch (consentWriteErr) {
        console.error("legalConsent updateDoc failed:", consentWriteErr);
      }

      // Re-resolve the authoritative users/{uid} access state (gender is
      // now set server-side) and AWAIT it before navigating - this is
      // what prevents the /home <-> /select-gender navigation loop:
      // without it, RequireGender's guard on /home would still see the
      // pre-onboarding "needs_gender" reason (cached since this provider
      // mounted once, above the route tree, and never unmounts) and bounce
      // straight back here. Awaiting first means the single render that
      // follows navigate("/home") already has the correct, up-to-date
      // access/reason - including routing an unverified female straight to
      // /verification via RequireGender's own existing redirect, exactly
      // once, with no extra branching needed here. This fix (not any
      // country logic) is what resolves the real published v11 redirect/
      // loading flash loop - see completeOnboarding.js's header.
      if (typeof refreshAccountSessionState === "function") {
        await refreshAccountSessionState();
      }

      navigate("/home");
    } catch (e) {
      console.error("completeOnboarding error:", e);

      const code = e?.code || "";
      if (code === "functions/permission-denied") {
        alert(t("gender.minor_denied", { defaultValue: "Minors are not allowed. Account creation is not available." }));
      } else if (code === "functions/failed-precondition") {
        alert(t("gender.locked", { defaultValue: "These details cannot be changed after they are set once." }));
      } else if (code === "functions/invalid-argument") {
        alert(t("gender.invalid_birthdate_country", { defaultValue: "Check the birthdate, then try again." }));
      } else {
        alert(t("gender.complete_error", { defaultValue: "An error occurred while completing registration. Please try again." }));
      }

      submitInFlightRef.current = false;
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="sg-loading">{t("common.loading", { defaultValue: "Loading..." })}</div>;
  }

  return (
    <div className="sg-page">
      <div className="sg-bgGlow sg-bgGlowA" />
      <div className="sg-bgGlow sg-bgGlowB" />

      <div className="sg-card">
        <div className="sg-titleWrap">
          <h2 className="sg-title">{t("gender.title", { defaultValue: "Choose gender" })}</h2>
          <div className="sg-sub">{t("gender.subtitle", { defaultValue: "Select your gender" })}</div>
        </div>

        <div className="sg-field">
          <label className="sg-label" htmlFor="sg-name-input">
            {t("gender.name", { defaultValue: "Name" })}
          </label>
          <input
            id="sg-name-input"
            type="text"
            className="sg-textInput"
            value={name}
            placeholder={t("gender.name_placeholder", {
              defaultValue: "Enter your name",
            })}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="name"
            aria-label={t("gender.name", { defaultValue: "Name" })}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className="sg-field">
          <label className="sg-label">{t("gender.birthdate", { defaultValue: "Birthdate" })}</label>

          <div className="sg-dateRow">
            <input
              type="text"
              inputMode="numeric"
              className="sg-dateInput sg-dateTextInput"
              value={birthDateText}
              placeholder={t("gender.birthdate_placeholder", {
                defaultValue: "DD.MM.YYYY",
              })}
              maxLength={10}
              autoComplete="bday"
              aria-label={t("gender.birthdate", { defaultValue: "Birthdate" })}
              onChange={(e) => {
                const formatted = formatBirthdateTyping(e.target.value);
                setBirthDateText(formatted);
                const iso = parseDisplayToIso(formatted);
                if (iso) {
                  setBirthDate(iso);
                  localStorage.setItem("birthDate", iso);
                } else {
                  setBirthDate("");
                  localStorage.removeItem("birthDate");
                }
              }}
              disabled={busy}
            />
            <input
              ref={nativeDateRef}
              type="date"
              className="sg-dateNative"
              value={birthDate}
              max={todayISO}
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const v = e.target.value;
                setBirthDate(v);
                setBirthDateText(isoToDisplay(v));
                if (v) {
                  localStorage.setItem("birthDate", v);
                } else {
                  localStorage.removeItem("birthDate");
                }
              }}
              disabled={busy}
            />
            <button
              type="button"
              className="sg-dateIcon"
              disabled={busy}
              aria-label={t("gender.birthdate", { defaultValue: "Birthdate" })}
              onClick={() => {
                const el = nativeDateRef.current;
                if (!el) return;
                if (typeof el.showPicker === "function") {
                  el.showPicker();
                } else {
                  el.click();
                }
              }}
            >
              📅
            </button>
          </div>

          <div className="sg-hint">{t("gender.age_hint", { defaultValue: "You must be 18+ years old" })}</div>
        </div>

        <div className="sg-options">
          <button
            type="button"
            className={`sg-option sg-female${selectedGender === "female" ? " sg-option-selected" : ""}`}
            disabled={busy}
            aria-pressed={selectedGender === "female"}
            onClick={() => setSelectedGender("female")}
          >
            <span className="sg-avatar">
              <img src={femaleAvatar} alt={t("gender.female", { defaultValue: "Female" })} />
            </span>
            <span className="sg-optText">
              <span className="sg-optAr">{t("gender.female", { defaultValue: "Female" })}</span>
            </span>
            <span className="sg-chev">›</span>
          </button>

          <button
            type="button"
            className={`sg-option sg-male${selectedGender === "male" ? " sg-option-selected" : ""}`}
            disabled={busy}
            aria-pressed={selectedGender === "male"}
            onClick={() => setSelectedGender("male")}
          >
            <span className="sg-avatar">
              <img src={maleAvatar} alt={t("gender.male", { defaultValue: "Male" })} />
            </span>
            <span className="sg-optText">
              <span className="sg-optAr">{t("gender.male", { defaultValue: "Male" })}</span>
            </span>
            <span className="sg-chev">›</span>
          </button>
        </div>

        <button
          type="button"
          className="sg-continueBtn"
          disabled={busy}
          onClick={handleContinue}
        >
          {busy
            ? t("common.loading", { defaultValue: "Loading..." })
            : t("gender.continue", { defaultValue: "Continue" })}
        </button>

        <div className="sg-card-bottom">
          <div className="sg-consent">
            {consentError ? (
              <p className="sg-consent-error" role="alert">
                {consentError}
              </p>
            ) : null}
            <label className="sg-consent-label">
              <input
                type="checkbox"
                className="sg-consent-checkbox"
                checked={legalConsentChecked}
                disabled={busy}
                onChange={(e) => {
                  setLegalConsentChecked(e.target.checked);
                  if (e.target.checked) setConsentError("");
                }}
              />
              <span className="sg-consent-text">
                <Trans
                  i18nKey="gender.consent_checkbox"
                  defaults="I confirm that I am 18+ and agree to the <termsLink>Terms of Use</termsLink> and <privacyLink>Privacy Policy</privacyLink>."
                  components={{
                    termsLink: <Link to="/terms" className="livora-legal-link" />,
                    privacyLink: <Link to="/privacy" className="livora-legal-link" />,
                  }}
                />
              </span>
            </label>
          </div>

          <div className="sg-footerNote">
            {t("gender.footer_note", { defaultValue: "Safe ? Secure ? Private" })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectGenderPage;
